import api from '../api';
import type {
    WritingPrompt,
    WritingSession,
    WritingAttempt,
    WritingFeedback,
    UserWritingProfile,
    WritingRecommendation,
    WritingMiniGame,
    WritingMiniGameAttempt,
    WritingPromptFilters,
    StartWritingSessionRequest,
    StartWritingSessionResponse,
    SubmitWritingAttemptRequest,
    SubmitWritingRequest,
    WritingFeedbackResponse,
    WritingAnalysisRequest,
    WritingAnalysisResponse,
    WritingRecommendationRequest,
    UpdateWritingProfileRequest,
    WritingAnalyticsRequest,
    WritingAnalyticsResponse,
    WritingProgressTimelineRequest,
    WritingProgressTimelineResponse,
    WritingSkillBreakdownResponse,
    PlayMiniGameRequest,
    SubmitMiniGameAnswerRequest,
    RealTimeFeedbackRequest,
    RealTimeFeedbackResponse,
    AIAvatarResponse,
    WritingAPIResponse,
    PaginatedResponse,
    PaginationParams,
} from './types';
const baseUrl = '/writing';
class WritingService {
    // === WRITING PROMPTS ===
    async getWritingPrompts(
        filters?: WritingPromptFilters & PaginationParams,
    ): Promise<PaginatedResponse<WritingPrompt>> {
        const response = await api.get(`${baseUrl}/prompts/`, { params: filters });
        return response.data;
    }

    async getWritingPromptById(promptId: string): Promise<WritingPrompt> {
        const response = await api.get(`${baseUrl}/prompts/${promptId}/`);
        return response.data;
    }

    async searchWritingPrompts(query: string, filters?: WritingPromptFilters): Promise<WritingPrompt[]> {
        const response = await api.get(`${baseUrl}/prompts/search/`, {
            params: { q: query, ...filters },
        });
        return response.data.results;
    }

    // === WRITING SESSIONS ===
    async getWritingSessions(params?: PaginationParams): Promise<PaginatedResponse<WritingSession>> {
        const response = await api.get(`${baseUrl}/sessions/`, { params });
        return response.data;
    }

    async getWritingSessionById(sessionId: string): Promise<WritingSession> {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/`);
        return response.data;
    }

    async startWritingSession(request: StartWritingSessionRequest): Promise<StartWritingSessionResponse> {
        const response = await api.post(`${baseUrl}/sessions/start/`, request);
        return response.data;
    }

    async createWritingSession(request: {
        prompt_id: string;
        session_type: string;
        difficulty_level: string;
    }): Promise<WritingSession> {
        const response = await api.post(`${baseUrl}/sessions/create/`, request);
        return response.data;
    }

    async completeWritingSession(sessionId: string, completionData?: any): Promise<any> {
        const response = await api.post(`${baseUrl}/sessions/${sessionId}/complete-session/`, completionData || {});
        return response.data;
    }

    async abandonWritingSession(sessionId: string): Promise<WritingSession> {
        const response = await api.patch(`${baseUrl}/sessions/${sessionId}/abandon/`);
        return response.data;
    }

    // === WRITING ATTEMPTS ===
    async getSessionAttempts(sessionId: string): Promise<WritingAttempt[]> {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/attempts/`);
        return response.data.results;
    }

    async submitWritingAttempt(request: SubmitWritingAttemptRequest): Promise<WritingAttempt> {
        const response = await api.post(`${baseUrl}/attempts/`, request);
        return response.data;
    }

    async submitWriting(request: SubmitWritingRequest): Promise<WritingFeedbackResponse> {
        const response = await api.post(`${baseUrl}/submit/`, request);
        return response.data;
    }

    async updateWritingAttempt(attemptId: string, content: string): Promise<WritingAttempt> {
        const response = await api.patch(`${baseUrl}/attempts/${attemptId}/`, { content });
        return response.data;
    }

    async getWritingAttemptById(attemptId: string): Promise<WritingAttempt> {
        const response = await api.get(`${baseUrl}/attempts/${attemptId}/`);
        return response.data;
    }

    // === WRITING FEEDBACK ===
    async getWritingFeedback(attemptId: string): Promise<WritingFeedback> {
        const response = await api.get(`${baseUrl}/attempts/${attemptId}/feedback/`);
        return response.data;
    }

    async generateWritingFeedback(attemptId: string): Promise<WritingFeedback> {
        const response = await api.post(`${baseUrl}/attempts/${attemptId}/generate-feedback/`);
        return response.data;
    }

    async requestAIVoiceFeedback(attemptId: string): Promise<AIAvatarResponse> {
        const response = await api.post(`${baseUrl}/attempts/${attemptId}/voice-feedback/`);
        return response.data;
    }

    // === REAL-TIME ANALYSIS ===
    async analyzeWritingRealTime(request: RealTimeFeedbackRequest): Promise<RealTimeFeedbackResponse> {
        const response = await api.post(`${baseUrl}/analyze-realtime/`, request);
        return response.data;
    }

    async analyzeWritingText(request: WritingAnalysisRequest): Promise<WritingAnalysisResponse> {
        const response = await api.post(`${baseUrl}/analyze/`, request);
        return response.data;
    }

    // === USER PROFILE ===
    async getUserWritingProfile(): Promise<UserWritingProfile> {
        const response = await api.get(`${baseUrl}/profile/`);
        return response.data;
    }

    async updateUserWritingProfile(updateData: UpdateWritingProfileRequest): Promise<UserWritingProfile> {
        const response = await api.patch(`${baseUrl}/profile/`, updateData);
        return response.data;
    }

    async resetUserWritingProfile(): Promise<UserWritingProfile> {
        const response = await api.post(`${baseUrl}/profile/reset/`);
        return response.data;
    }

    // === RECOMMENDATIONS ===
    async getWritingRecommendations(params?: WritingRecommendationRequest): Promise<WritingRecommendation[]> {
        const response = await api.get(`${baseUrl}/recommendations/`, { params });
        return response.data.results;
    }

    async rateWritingRecommendation(recommendationId: string, rating: number): Promise<WritingRecommendation> {
        const response = await api.patch(`${baseUrl}/recommendations/${recommendationId}/rate/`, { rating });
        return response.data;
    }

    async markRecommendationCompleted(recommendationId: string): Promise<WritingRecommendation> {
        const response = await api.patch(`${baseUrl}/recommendations/${recommendationId}/complete/`);
        return response.data;
    }

    async generatePersonalizedRecommendations(): Promise<WritingRecommendation[]> {
        const response = await api.post(`${baseUrl}/recommendations/generate/`);
        return response.data.results;
    }

    // === ANALYTICS ===
    async getWritingAnalytics(params?: WritingAnalyticsRequest): Promise<WritingAnalyticsResponse> {
        const response = await api.get(`${baseUrl}/analytics/`, { params });
        return response.data;
    }

    async getWritingProgressTimeline(
        params?: WritingProgressTimelineRequest,
    ): Promise<WritingProgressTimelineResponse> {
        const response = await api.get(`${baseUrl}/analytics/timeline/`, { params });
        return response.data;
    }

    async getWritingSkillBreakdown(): Promise<WritingSkillBreakdownResponse> {
        const response = await api.get(`${baseUrl}/analytics/skills/`);
        return response.data;
    }

    async getWeeklyWritingProgress(weeks: number = 12): Promise<any> {
        const response = await api.get(`${baseUrl}/analytics/weekly/`, { params: { weeks } });
        return response.data;
    }

    async getMonthlyWritingProgress(months: number = 6): Promise<any> {
        const response = await api.get(`${baseUrl}/analytics/monthly/`, { params: { months } });
        return response.data;
    }

    async getWritingStreakData(): Promise<any> {
        const response = await api.get(`${baseUrl}/analytics/streak/`);
        return response.data;
    }

    // === MINI GAMES ===
    async getWritingMiniGames(difficultyLevel?: string): Promise<WritingMiniGame[]> {
        const response = await api.get(`${baseUrl}/mini-games/`, {
            params: difficultyLevel ? { difficulty_level: difficultyLevel } : undefined,
        });
        return response.data.results;
    }

    async getWritingMiniGameById(gameId: string): Promise<WritingMiniGame> {
        const response = await api.get(`${baseUrl}/mini-games/${gameId}/`);
        return response.data;
    }

    async playWritingMiniGame(request: PlayMiniGameRequest): Promise<WritingMiniGameAttempt> {
        const response = await api.post(`${baseUrl}/mini-games/play/`, request);
        return response.data;
    }

    async submitMiniGameAnswer(request: SubmitMiniGameAnswerRequest): Promise<WritingMiniGameAttempt> {
        const response = await api.patch(`${baseUrl}/mini-games/attempts/${request.attempt_id}/submit/`, {
            answers: request.answers,
        });
        return response.data;
    }

    async getUserMiniGameAttempts(gameId?: string): Promise<WritingMiniGameAttempt[]> {
        const response = await api.get(`${baseUrl}/mini-games/attempts/`, {
            params: gameId ? { mini_game: gameId } : undefined,
        });
        return response.data.results;
    }

    // === AI AVATAR INTERACTIONS ===
    async getAIPromptIntroduction(promptId: string): Promise<AIAvatarResponse> {
        const response = await api.get(`${baseUrl}/prompts/${promptId}/ai-introduction/`);
        return response.data;
    }

    async getAIEncouragement(sessionId: string): Promise<AIAvatarResponse> {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/ai-encouragement/`);
        return response.data;
    }

    async getAICelebration(sessionId: string): Promise<AIAvatarResponse> {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/ai-celebration/`);
        return response.data;
    }

    async askAIAssistant(sessionId: string, question: string): Promise<AIAvatarResponse> {
        const response = await api.post(`${baseUrl}/sessions/${sessionId}/ask-assistant/`, { question });
        return response.data;
    }

    // === UTILITY METHODS ===
    async validateWritingText(
        text: string,
        writingType?: string,
    ): Promise<{
        isValid: boolean;
        errors: string[];
        wordCount: number;
    }> {
        const response = await api.post(`${baseUrl}/validate/`, {
            text,
            writing_type: writingType,
        });
        return response.data;
    }

    async getWritingTips(
        writingType: string,
        difficultyLevel?: string,
    ): Promise<{
        tips: string[];
        phrases: string[];
        structure: any;
    }> {
        const response = await api.get(`${baseUrl}/tips/`, {
            params: { writing_type: writingType, difficulty_level: difficultyLevel },
        });
        return response.data;
    }

    async exportWritingProgress(): Promise<Blob> {
        const response = await api.get(`${baseUrl}/export/`, {
            responseType: 'blob',
        });
        return response.data;
    }

    // === ERROR HANDLING WRAPPER ===
    private async handleApiCall<T>(apiCall: () => Promise<T>): Promise<WritingAPIResponse<T>> {
        try {
            const data = await apiCall();
            return {
                success: true,
                data,
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'An unexpected error occurred',
                message: error.response?.data?.message,
            };
        }
    }

    // === SAFE API METHODS (WITH ERROR HANDLING) ===
    async safeGetWritingPrompts(
        filters?: WritingPromptFilters & PaginationParams,
    ): Promise<WritingAPIResponse<PaginatedResponse<WritingPrompt>>> {
        return this.handleApiCall(() => this.getWritingPrompts(filters));
    }

    async safeStartWritingSession(
        request: StartWritingSessionRequest,
    ): Promise<WritingAPIResponse<StartWritingSessionResponse>> {
        return this.handleApiCall(() => this.startWritingSession(request));
    }

    async safeSubmitWritingAttempt(request: SubmitWritingAttemptRequest): Promise<WritingAPIResponse<WritingAttempt>> {
        return this.handleApiCall(() => this.submitWritingAttempt(request));
    }

    async safeGetWritingAnalytics(
        params?: WritingAnalyticsRequest,
    ): Promise<WritingAPIResponse<WritingAnalyticsResponse>> {
        return this.handleApiCall(() => this.getWritingAnalytics(params));
    }
}

export default new WritingService();
